import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Button, Stack, Tooltip, Chip, Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import ControlsBar from './components/ControlsBar';
import './App.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type Priority = 'low' | 'medium' | 'high';

interface Task {
  id:          string;
  title:       string;
  description: string;
  priority:    Priority;
  /** Epoch ms when reminder fires. null = no reminder. */
  remindAt:    number | null;
  /** Epoch ms when task was created. */
  createdAt:   number;
  /** Once a duration-based reminder fires it's done — no daily repeat. */
  notified:    boolean;
}

interface PhysState {
  x: number; y: number;
  vx: number; vy: number;
}

interface DragState {
  id:     string;
  r:      number;
  startX: number; startY: number;
  lastX:  number; lastY:  number; lastT: number;
  vx:     number; vy:     number;
  moved:  boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LS_KEY     = 'antiforget-tasks';
const POS_LS_KEY = 'antiforget-positions';

/** Bubble radius (px) per priority level. */
const BUBBLE_RADIUS: Record<Priority, number> = { low: 70, medium: 102, high: 138 };

const EMPTY_DRAFT = {
  title:       '',
  description: '',
  priority:    'medium' as Priority,
  durationMs:  null as number | null,
  atTime:      null as string | null,   // HH:MM for "at specific clock time" mode
};

const PRESET_MS: { key: string; ms: number }[] = [
  { key: '15m', ms: 15 * 60 * 1000 },
  { key: '30m', ms: 30 * 60 * 1000 },
  { key: '1h',  ms:      60 * 60 * 1000 },
  { key: '2h',  ms:  2 * 60 * 60 * 1000 },
];

// ── Notification chime — C5 → E5 → G5 → C6 major arpeggio via Web Audio API ──
const playChime = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc      = ctx.createOscillator();
      const gain     = ctx.createGain();
      const isLast   = i === notes.length - 1;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t      = ctx.currentTime + i * 0.13;
      const vol    = isLast ? 0.20 : 0.28;   // C6 a touch softer — feels like resolution
      const decay  = isLast ? 1.10 : 0.70;   // C6 lingers longer
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(vol, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.001, t + decay);
      osc.start(t);
      osc.stop(t + decay);
    });
  } catch { /* AudioContext unavailable */ }
};

// Physics
const DAMPING     = 0.9993;
const RESTITUTION = 0.82;
const MAX_SPEED   = 220;
const MIN_SPEED   = 22;
const INSET_TOP   = 76;
const INSET_SIDE  = 14;
const INSET_BOT   = 30;
const DRAG_THRESH = 6;   // px before press becomes a drag

// Countdown clock — 500 ms is imperceptible lag without thrashing every frame
const COUNTDOWN_TICK_MS = 500;
const IMMINENT_MS       = 60 * 1000;     // pulsing red at < 1 min
const AUTO_DISMISS_MS   = 3 * 60 * 1000; // remove "now" bubbles 3 min after they fire

// ─── Helpers ─────────────────────────────────────────────────────────────────

const uid = () => `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const formatRemaining = (ms: number): string => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

// Responsive scale — phones get smaller bubbles so they don't crowd the
// limited viewport. Recomputed on resize / orientation change.
const computeScale = (): number => {
  if (typeof window === 'undefined') return 1.0;
  const w = window.innerWidth;
  if (w < 380) return 0.55;
  if (w < 500) return 0.66;
  if (w < 700) return 0.80;
  return 1.0;
};

// ── Sphere gradient — 4-stop radial, lit from upper-left ─────────────────────
// Light mode: crisp white specular → vivid priority color → deeper warm edge.
// Dark mode: dimmer specular, deeper body, colored glow in shadow.
const bubbleStyle = (
  p: Priority,
  isDark: boolean,
): { bg: string; border: string; shadow: string } => {

  if (p === 'low') return isDark
    ? {
        bg:     'radial-gradient(circle at 34% 30%, rgba(130,96,56,0.16) 0%, rgba(70,44,18,0.12) 48%, rgba(10,5,1,0.56) 100%)',
        border: 'rgba(160,124,80,0.14)',
        shadow: '0 20px 56px rgba(0,0,0,0.72), 0 8px 20px rgba(0,0,0,0.44), inset 0 1px 0 rgba(200,170,130,0.08), inset 0 0 18px rgba(120,80,30,0.04)',
      }
    : {
        bg:     'radial-gradient(circle at 34% 30%, rgba(255,246,228,0.82) 0%, rgba(238,214,182,0.78) 28%, rgba(214,186,150,0.74) 58%, rgba(188,156,116,0.70) 100%)',
        border: 'rgba(230,210,180,0.70)',
        shadow: '0 16px 44px rgba(110,78,42,0.18), 0 6px 16px rgba(90,60,28,0.12), 0 0 0 0.5px rgba(200,170,130,0.28), inset 0 0 20px rgba(255,240,210,0.10)',
      };

  if (p === 'medium') return isDark
    ? {
        bg:     'radial-gradient(circle at 34% 30%, rgba(170,118,12,0.20) 0%, rgba(110,62,4,0.14) 46%, rgba(50,18,0,0.58) 100%)',
        border: 'rgba(190,138,20,0.18)',
        shadow: '0 20px 56px rgba(0,0,0,0.64), 0 0 28px rgba(190,100,10,0.12), inset 0 1px 0 rgba(240,180,70,0.07), inset 0 0 18px rgba(150,90,10,0.04)',
      }
    : {
        bg:     'radial-gradient(circle at 34% 30%, rgba(255,232,148,0.86) 0%, rgba(255,198,72,0.82) 28%, rgba(248,162,28,0.78) 58%, rgba(230,128,12,0.72) 100%)',
        border: 'rgba(255,200,80,0.72)',
        shadow: '0 16px 44px rgba(170,98,10,0.22), 0 6px 16px rgba(175,100,14,0.14), 0 0 24px rgba(255,170,40,0.14), inset 0 0 20px rgba(255,230,140,0.08)',
      };

  // high
  return isDark
    ? {
        bg:     'radial-gradient(circle at 34% 30%, rgba(200,110,10,0.24) 0%, rgba(150,54,4,0.18) 44%, rgba(80,14,0,0.62) 100%)',
        border: 'rgba(210,100,16,0.22)',
        shadow: '0 20px 56px rgba(0,0,0,0.60), 0 0 34px rgba(200,72,6,0.16), inset 0 1px 0 rgba(255,150,40,0.07), inset 0 0 18px rgba(160,58,6,0.06)',
      }
    : {
        bg:     'radial-gradient(circle at 34% 30%, rgba(255,210,120,0.84) 0%, rgba(255,158,44,0.82) 24%, rgba(245,112,18,0.80) 54%, rgba(220,68,8,0.76) 100%)',
        border: 'rgba(255,170,70,0.72)',
        shadow: '0 16px 44px rgba(200,64,8,0.26), 0 6px 16px rgba(195,62,8,0.16), 0 0 30px rgba(255,110,22,0.18), inset 0 0 20px rgba(255,210,130,0.08)',
      };
};

// ─── Inner app ────────────────────────────────────────────────────────────────

const AntiforgetApp: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const muiTheme     = useMuiTheme();
  const isDark       = muiTheme.palette.mode === 'dark';

  const [tasks, setTasks] = useState<Task[]>(() => {
    try {
      // Migrate legacy entries that used `time: "HH:MM"` to `remindAt` epoch ms.
      const raw  = JSON.parse(localStorage.getItem(LS_KEY) || '[]') as Partial<Task & { time?: string }>[];
      const nowMs = Date.now();
      return raw.map(r => {
        let remindAt: number | null = (r as { remindAt?: number | null }).remindAt ?? null;
        const legacyTime = (r as { time?: string }).time;
        if (remindAt === null && legacyTime && /^\d{2}:\d{2}$/.test(legacyTime)) {
          const [hh, mm] = legacyTime.split(':').map(Number);
          const at = new Date();
          at.setHours(hh, mm, 0, 0);
          remindAt = at.getTime() > nowMs ? at.getTime() : null;
        }
        return {
          id:          r.id          ?? uid(),
          title:       r.title       ?? '',
          description: r.description ?? '',
          priority:    (r.priority   ?? 'medium') as Priority,
          remindAt,
          createdAt:   r.createdAt   ?? nowMs,
          notified:    r.notified    ?? false,
        };
      });
    } catch { return []; }
  });

  const [modalOpen,     setModalOpen]     = useState(false);
  const [editingId,     setEditingId]     = useState<string | null>(null);
  const [draft,         setDraft]         = useState({ ...EMPTY_DRAFT });
  const [titleError,    setTitleError]    = useState(false);
  const [clearConfirm,  setClearConfirm]  = useState(false);
  const [dismissConfirmId, setDismissConfirmId] = useState<string | null>(null);
  const dismissConfirmTimerRef = useRef<number | null>(null);
  const [notifPerm,    setNotifPerm]    = useState<NotificationPermission | 'unsupported'>(
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'unsupported'
  );

  const [scale, setScale] = useState<number>(() => computeScale());
  useEffect(() => {
    const onResize = () => setScale(computeScale());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const hasTimedTask = tasks.some(task => task.remindAt !== null);

  // ── Physics + drag refs ────────────────────────────────────────────────────
  const physicsRef  = useRef<Map<string, PhysState>>(new Map());
  const bubbleRefs  = useRef<Map<string, HTMLElement>>(new Map());
  const canvasRef   = useRef<HTMLDivElement>(null);
  const tasksRef       = useRef<Task[]>([]);
  const rafRef         = useRef<number | null>(null);
  const lastTimeRef    = useRef<number | null>(null);
  const dragRef        = useRef<DragState | null>(null);
  const nextImpulseRef = useRef<number>(4 + Math.random() * 5);

  // Scale is read every rAF frame so resize-driven shrinks/grows apply live.
  const scaleRef   = useRef<number>(scale);
  scaleRef.current = scale;

  /** Current scaled radius for a priority (px). */
  const radiusFor = useCallback(
    (priority: Priority) => Math.round(BUBBLE_RADIUS[priority] * scaleRef.current),
    [],
  );

  tasksRef.current = tasks;

  // ── LocalStorage ───────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(tasks)); }, [tasks]);

  // ── Position persistence ───────────────────────────────────────────────────
  // Saved positions are loaded once on mount and used by the physics init
  // effect to restore bubble layout across page refreshes. We persist on a 4s
  // interval plus on pagehide / visibility-hide — pagehide is the only event
  // guaranteed to fire on mobile Safari when the tab is suspended.
  const savedPositions = useMemo<Record<string, PhysState>>(() => {
    try { return JSON.parse(localStorage.getItem(POS_LS_KEY) || '{}'); }
    catch { return {}; }
  }, []);

  useEffect(() => {
    const save = () => {
      const positions: Record<string, PhysState> = {};
      physicsRef.current.forEach((s, id) => {
        positions[id] = { x: s.x, y: s.y, vx: s.vx, vy: s.vy };
      });
      try { localStorage.setItem(POS_LS_KEY, JSON.stringify(positions)); } catch {}
    };
    const intervalId = window.setInterval(save, 4000);
    const onHide     = () => save();
    const onVis      = () => { if (document.hidden) save(); };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVis);
      save();
    };
  }, []);

  // ── Notification permission — request proactively on mount ────────────────
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
        .then(p => setNotifPerm(p))
        .catch(() => {});
    }
  }, []);

  // ── Notification timer ─────────────────────────────────────────────────────
  useEffect(() => {
    const fire = () => {
      const canNotify = 'Notification' in window && Notification.permission === 'granted';
      const now = Date.now();
      setTasks(prev => {
        let changed = false;
        const mapped = prev.map(task => {
          if (canNotify && task.remindAt !== null && !task.notified && now >= task.remindAt) {
            try {
              new Notification('Antiforget 🔔', {
                body: task.title,
                icon: `${process.env.PUBLIC_URL}/icon.svg`,
                tag:  task.id,
              });
            } catch (err) {
              console.warn('Antiforget: notification failed', err);
            }
            playChime();
            changed = true;
            return { ...task, notified: true };
          }
          return task;
        });
        // Auto-dismiss tasks that have been "now" for more than AUTO_DISMISS_MS.
        const filtered = mapped.filter(task => {
          if (task.remindAt !== null && now - task.remindAt > AUTO_DISMISS_MS) {
            changed = true;
            return false;
          }
          return true;
        });
        return changed ? filtered : prev;
      });
    };

    fire();
    const id = setInterval(fire, 5_000);
    const onVisible = () => { if (!document.hidden) fire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  // ── Countdown ticker ───────────────────────────────────────────────────────
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ── Physics init ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const W = canvas ? canvas.clientWidth  : window.innerWidth;
    const H = canvas ? canvas.clientHeight : window.innerHeight;

    tasks.forEach(task => {
      if (!physicsRef.current.has(task.id)) {
        const r     = radiusFor(task.priority);
        const saved = savedPositions[task.id];

        if (saved
            && Number.isFinite(saved.x)  && Number.isFinite(saved.y)
            && Number.isFinite(saved.vx) && Number.isFinite(saved.vy)) {
          // Clamp restored position to current viewport — handles rotation /
          // device-change between sessions.
          const minX = r + INSET_SIDE, maxX = Math.max(minX, W - r - INSET_SIDE);
          const minY = r + INSET_TOP,  maxY = Math.max(minY, H - r - INSET_BOT);
          physicsRef.current.set(task.id, {
            x:  Math.min(Math.max(saved.x, minX), maxX),
            y:  Math.min(Math.max(saved.y, minY), maxY),
            vx: saved.vx,
            vy: saved.vy,
          });
        } else {
          const availW = Math.max(0, W - 2 * r - 2 * INSET_SIDE);
          const availH = Math.max(0, H - 2 * r - INSET_TOP - INSET_BOT);
          const x      = r + INSET_SIDE + Math.random() * availW;
          const y      = r + INSET_TOP  + Math.random() * availH;
          const speed  = 40 + Math.random() * 40;
          const angle  = Math.random() * Math.PI * 2;
          physicsRef.current.set(task.id, {
            x, y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
          });
        }
      }
    });

    Array.from(physicsRef.current.keys()).forEach(id => {
      if (!tasks.find(t => t.id === id)) physicsRef.current.delete(id);
    });
  }, [tasks, savedPositions, radiusFor]);

  // ── rAF physics loop ───────────────────────────────────────────────────────
  const hasTasks = tasks.length !== 0;
  useEffect(() => {
    if (!hasTasks) {
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      lastTimeRef.current = null;
      return;
    }

    const loop = (ts: number) => {
      const dt = Math.min((ts - (lastTimeRef.current ?? ts)) / 1000, 0.05);
      lastTimeRef.current = ts;

      const canvas = canvasRef.current;
      if (!canvas) { rafRef.current = requestAnimationFrame(loop); return; }
      const W = canvas.clientWidth;
      const H = canvas.clientHeight;
      const dragId = dragRef.current?.id ?? null;

      type Entry = { id: string; r: number; s: PhysState };
      const entries: Entry[] = [];
      for (const task of tasksRef.current) {
        const s = physicsRef.current.get(task.id);
        if (!s) continue;
        entries.push({ id: task.id, r: Math.round(BUBBLE_RADIUS[task.priority] * scaleRef.current), s });
      }

      // ── Integrate + wall bounce ────────────────────────────────────────
      for (const { id, r, s } of entries) {
        if (id === dragId) continue;

        s.x += s.vx * dt;
        s.y += s.vy * dt;

        if (s.x - r < INSET_SIDE)     { s.x = r + INSET_SIDE;     s.vx =  Math.abs(s.vx) * RESTITUTION; }
        if (s.x + r > W - INSET_SIDE) { s.x = W - r - INSET_SIDE; s.vx = -Math.abs(s.vx) * RESTITUTION; }
        if (s.y - r < INSET_TOP)      { s.y = r + INSET_TOP;       s.vy =  Math.abs(s.vy) * RESTITUTION; }
        if (s.y + r > H - INSET_BOT)  { s.y = H - r - INSET_BOT;  s.vy = -Math.abs(s.vy) * RESTITUTION; }

        s.vx *= DAMPING;
        s.vy *= DAMPING;

        const spd = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
        if (spd < MIN_SPEED) {
          if (spd > 0) { const sc = MIN_SPEED / spd; s.vx *= sc; s.vy *= sc; }
          else { const a = Math.random() * Math.PI * 2; s.vx = Math.cos(a) * MIN_SPEED; s.vy = Math.sin(a) * MIN_SPEED; }
        } else if (spd > MAX_SPEED) {
          const sc = MAX_SPEED / spd; s.vx *= sc; s.vy *= sc;
        }
      }

      // ── Occasional random impulse — kicks 1-2 bubbles every 4-9 s ─────
      nextImpulseRef.current -= dt;
      if (nextImpulseRef.current <= 0 && entries.length > 0) {
        const count = Math.min(entries.length, 1 + Math.round(Math.random()));
        for (let k = 0; k < count; k++) {
          const e = entries[Math.floor(Math.random() * entries.length)];
          if (e.id === dragId) continue;
          const angle = Math.random() * Math.PI * 2;
          const kick  = MAX_SPEED * (0.55 + Math.random() * 0.45);
          e.s.vx += Math.cos(angle) * kick;
          e.s.vy += Math.sin(angle) * kick;
        }
        nextImpulseRef.current = 4 + Math.random() * 5;
      }

      // ── Circle-circle collision ────────────────────────────────────────
      for (let i = 0; i < entries.length - 1; i++) {
        for (let j = i + 1; j < entries.length; j++) {
          const { id: idA, r: ra, s: a } = entries[i];
          const { id: idB, r: rb, s: b } = entries[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const distSq  = dx * dx + dy * dy;
          const minDist = ra + rb;
          if (distSq >= minDist * minDist || distSq === 0) continue;

          const dist = Math.sqrt(distSq);
          const nx = dx / dist, ny = dy / dist;
          const push = (minDist - dist) * 0.5;

          const isPinnedA = idA === dragId;
          const isPinnedB = idB === dragId;

          if (!isPinnedA) { a.x -= nx * push; a.y -= ny * push; }
          if (!isPinnedB) { b.x += nx * push; b.y += ny * push; }

          const ma = ra * ra, mb = rb * rb, totalM = ma + mb;
          const dvx = a.vx - b.vx, dvy = a.vy - b.vy;
          const dot = dvx * nx + dvy * ny;
          if (dot > 0) {
            if (isPinnedA) {
              const vbn = b.vx * nx + b.vy * ny;
              b.vx -= (1 + RESTITUTION) * vbn * nx;
              b.vy -= (1 + RESTITUTION) * vbn * ny;
            } else if (isPinnedB) {
              const van = a.vx * nx + a.vy * ny;
              a.vx -= (1 + RESTITUTION) * van * nx;
              a.vy -= (1 + RESTITUTION) * van * ny;
            } else {
              const imp = (1 + RESTITUTION) * dot / totalM;
              a.vx -= imp * mb * nx; a.vy -= imp * mb * ny;
              b.vx += imp * ma * nx; b.vy += imp * ma * ny;
            }
          }
        }
      }

      // ── Apply transforms ───────────────────────────────────────────────
      for (const { id, r, s } of entries) {
        const el = bubbleRefs.current.get(id);
        if (el) el.style.transform = `translate(${s.x - r}px,${s.y - r}px)`;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTasks]);

  // ── Drag handlers ──────────────────────────────────────────────────────────

  const handlePointerDown = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx   = e.clientX - rect.left;
    const cy   = e.clientY - rect.top;
    const task = tasksRef.current.find(t => t.id === id);
    const r    = task ? radiusFor(task.priority) : 100;
    dragRef.current = {
      id, r,
      startX: cx, startY: cy,
      lastX: cx, lastY: cy, lastT: e.timeStamp,
      vx: 0, vy: 0, moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx   = e.clientX - rect.left;
    const cy   = e.clientY - rect.top;

    const dt = (e.timeStamp - drag.lastT) / 1000;
    if (dt > 0.001) {
      drag.vx = (cx - drag.lastX) / dt;
      drag.vy = (cy - drag.lastY) / dt;
    }
    if (Math.abs(cx - drag.startX) > DRAG_THRESH || Math.abs(cy - drag.startY) > DRAG_THRESH) {
      drag.moved = true;
    }
    drag.lastX = cx; drag.lastY = cy; drag.lastT = e.timeStamp;

    const s = physicsRef.current.get(drag.id);
    if (s) { s.x = cx; s.y = cy; }

    const el = bubbleRefs.current.get(drag.id);
    if (el) el.style.transform = `translate(${cx - drag.r}px,${cy - drag.r}px)`;
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    document.body.style.cursor = '';

    const s = physicsRef.current.get(drag.id);
    if (s) {
      const spd = Math.sqrt(drag.vx * drag.vx + drag.vy * drag.vy);
      const cap = spd > MAX_SPEED ? MAX_SPEED / spd : 1;
      s.vx = drag.vx * cap;
      s.vy = drag.vy * cap;
    }

    if (!drag.moved) {
      const task = tasksRef.current.find(t => t.id === drag.id);
      if (task) openEdit(task);
    }
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
    setTitleError(false);
    setModalOpen(true);
  };

  const openEdit = useCallback((task: Task) => {
    setEditingId(task.id);
    let durationMs: number | null = null;
    let atTime: string | null = null;
    if (task.remindAt !== null && !task.notified) {
      const remaining = Math.max(0, task.remindAt - Date.now());
      const isPreset  = PRESET_MS.some(p => Math.abs(p.ms - remaining) < 5000);
      if (isPreset) {
        durationMs = PRESET_MS.find(p => Math.abs(p.ms - remaining) < 5000)!.ms;
      } else {
        // Restore as clock time
        const d = new Date(task.remindAt);
        atTime = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
      }
    }
    setDraft({
      title:       task.title,
      description: task.description,
      priority:    task.priority,
      durationMs,
      atTime,
    });
    setTitleError(false);
    setModalOpen(true);
  }, []);

  const closeModal = () => setModalOpen(false);

  const handleSave = async () => {
    if (!draft.title.trim()) { setTitleError(true); return; }
    const hasReminder = draft.durationMs !== null || draft.atTime !== null;
    if (hasReminder && 'Notification' in window && Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    }
    let remindAt: number | null = null;
    if (draft.atTime) {
      const [hh, mm] = draft.atTime.split(':').map(Number);
      const target = new Date();
      target.setHours(hh, mm, 0, 0);
      if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
      remindAt = target.getTime();
    } else if (draft.durationMs !== null) {
      remindAt = Date.now() + draft.durationMs;
    }
    if (editingId) {
      setTasks(prev => prev.map(t => t.id === editingId
        ? {
            ...t,
            title:       draft.title,
            description: draft.description,
            priority:    draft.priority,
            remindAt,
            notified:    remindAt === null ? t.notified : false,
          }
        : t));
    } else {
      setTasks(prev => [...prev, {
        id:          uid(),
        title:       draft.title,
        description: draft.description,
        priority:    draft.priority,
        remindAt,
        createdAt:   Date.now(),
        notified:    false,
      }]);
    }
    closeModal();
  };

  const handleDelete = () => {
    if (!editingId) return;
    setTasks(prev => prev.filter(t => t.id !== editingId));
    closeModal();
  };

  const handleClear = useCallback(() => {
    if (clearConfirm) { setTasks([]); setClearConfirm(false); }
    else { setClearConfirm(true); setTimeout(() => setClearConfirm(false), 2800); }
  }, [clearConfirm]);

  // Two-tap × dismiss — first tap arms, second tap within 2.5s deletes.
  const handleDismissClick = useCallback((taskId: string) => {
    if (dismissConfirmId === taskId) {
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setDismissConfirmId(null);
      if (dismissConfirmTimerRef.current) {
        window.clearTimeout(dismissConfirmTimerRef.current);
        dismissConfirmTimerRef.current = null;
      }
    } else {
      setDismissConfirmId(taskId);
      if (dismissConfirmTimerRef.current) window.clearTimeout(dismissConfirmTimerRef.current);
      dismissConfirmTimerRef.current = window.setTimeout(() => {
        setDismissConfirmId(null);
        dismissConfirmTimerRef.current = null;
      }, 2500);
    }
  }, [dismissConfirmId]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <ControlsBar />

      {/* App title — top-left, always LTR */}
      <Box sx={{
        position: 'fixed', top: 16, left: 16,
        height: 40, display: 'flex', alignItems: 'center',
        direction: 'ltr',
        zIndex: 1199, pointerEvents: 'none',
      }}>
        <Typography sx={{
          fontFamily: '"Nunito", sans-serif',
          fontWeight: 900, fontSize: '1.05rem', letterSpacing: '-0.01em',
          color: isDark ? 'rgba(255,240,215,0.50)' : 'rgba(40,22,6,0.36)',
          userSelect: 'none',
        }}>
          Antiforget!
        </Typography>
      </Box>

      {/* Ambient orbs background */}
      <div className="ambient-stage" aria-hidden="true">
        <div className="orb orb-one" />
        <div className="orb orb-two" />
        <div className="orb orb-three" />
      </div>

      {/* Notification permission banner */}
      {hasTimedTask && notifPerm !== 'granted' && notifPerm !== 'unsupported' && (
        <Box sx={{
          position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1198, maxWidth: 'min(560px, calc(100vw - 32px))',
          px: 2.5, py: 1.25, borderRadius: '999px',
          display: 'flex', alignItems: 'center', gap: 1.5,
          backgroundColor: isDark ? 'rgba(255,138,42,0.16)' : 'rgba(255,232,202,0.92)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,138,42,0.32)' : 'rgba(255,160,60,0.45)',
          backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
          boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 24px rgba(200,120,30,0.16)',
        }}>
          <Box component="span" sx={{ fontSize: '1rem' }}>🔔</Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', flex: 1 }}>
            {notifPerm === 'denied'
              ? 'Notifications are blocked — enable them in your browser settings to get reminders.'
              : 'Allow notifications to get reminded at your set times.'}
          </Typography>
          {notifPerm === 'default' && (
            <Button
              size="small"
              onClick={() => Notification.requestPermission().then(p => setNotifPerm(p))}
              sx={{
                borderRadius: '999px', fontWeight: 700, fontSize: '0.74rem',
                textTransform: 'none', px: 1.5, minWidth: 0,
                background: 'linear-gradient(135deg, #ff9a45 0%, #ff7d22 100%)',
                color: '#1a0e08',
                '&:hover': { background: 'linear-gradient(135deg, #ffb06a 0%, #ff8a2a 100%)' },
              }}
            >
              Enable
            </Button>
          )}
        </Box>
      )}

      {/* Full-viewport physics canvas */}
      <Box ref={canvasRef} className="bubble-canvas">

        {tasks.length === 0 ? (
          /* Empty-state CTA bubble */
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
            <Box
              sx={{ animation: 'float0 9s ease-in-out infinite', animationDelay: '-3s', cursor: 'pointer' }}
              onClick={openAdd}
            >
              {(() => {
                const s = bubbleStyle('low', isDark);
                const ctaSize = Math.round(220 * scale);
                return (
                  <Box className="bubble" sx={{ width: ctaSize, height: ctaSize, background: s.bg, borderColor: s.border, boxShadow: s.shadow }}>
                    <Box className="bubble-shine"        aria-hidden="true" />
                    <Box className="bubble-shine-inner"  aria-hidden="true" />
                    <Box className="bubble-shine-bottom" aria-hidden="true" />
                    <Typography className="bubble-title" sx={{ fontSize: '1rem', px: '1.1rem' }}>
                      {t('fab.add')}
                    </Typography>
                    <Typography className="bubble-add-plus" aria-hidden="true">+</Typography>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        ) : (
          tasks.map(task => {
            const r    = radiusFor(task.priority);
            const D    = r * 2;
            const s    = bubbleStyle(task.priority, isDark);
            const fontSize = D < 160 ? '0.76rem' : D < 230 ? '0.90rem' : '1.06rem';
            const px       = D < 160 ? '0.72rem' : '1.1rem';

            const remainingMs   = task.remindAt !== null ? task.remindAt - now : null;
            const countdownText = remainingMs === null
              ? null
              : remainingMs <= 0
                ? t('countdown.now')
                : `${t('countdown.inPrefix')}${formatRemaining(remainingMs)}`;
            const isImminent = remainingMs !== null && remainingMs > 0 && remainingMs <= IMMINENT_MS;

            return (
              <Box
                key={task.id}
                className="bubble-anchor"
                ref={(el: Element | null) => {
                  if (el instanceof HTMLElement) bubbleRefs.current.set(task.id, el);
                  else bubbleRefs.current.delete(task.id);
                }}
                onPointerDown={(e: React.PointerEvent) => handlePointerDown(e, task.id)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                <Box
                  className="bubble"
                  sx={{ width: D, height: D, background: s.bg, borderColor: s.border, boxShadow: s.shadow }}
                >
                  <Box className="bubble-shine"        aria-hidden="true" />
                  <Box className="bubble-shine-inner"  aria-hidden="true" />
                  <Box className="bubble-shine-bottom" aria-hidden="true" />
                  <Typography className="bubble-title" sx={{ fontSize, px }}>
                    {task.title}
                  </Typography>
                  {countdownText && (
                    <Box className={`bubble-countdown${isImminent ? ' imminent' : ''}`}>
                      {countdownText}
                    </Box>
                  )}
                  <IconButton
                    aria-label={t('modal.delete')}
                    className={`bubble-dismiss${dismissConfirmId === task.id ? ' confirming' : ''}`}
                    onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDismissClick(task.id);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: D < 160 ? 14 : 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Clear all — bottom-left, two-tap confirm */}
      {tasks.length > 0 && (
        <Button
          onClick={handleClear}
          size="small"
          sx={{
            position: 'fixed', bottom: 36, left: 36,
            borderRadius: '999px', fontSize: '0.76rem', px: 2, py: 0.75,
            color:           clearConfirm ? 'error.main' : 'text.secondary',
            backgroundColor: clearConfirm
              ? (isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.07)')
              : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.55)'),
            border: '1px solid',
            borderColor: clearConfirm
              ? 'rgba(239,68,68,0.30)'
              : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)'),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: clearConfirm
                ? (isDark ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.12)')
                : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.80)'),
            },
          }}
        >
          {clearConfirm ? t('actions.clearConfirm') : t('actions.clearAll')}
        </Button>
      )}

      {/* Add FAB */}
      <Tooltip title={t('fab.add')} placement="left">
        <Fab onClick={openAdd} aria-label={t('fab.add')} sx={{
          position: 'fixed', bottom: 32, right: 32,
          background: 'linear-gradient(135deg, #ff9a45 0%, #ff7d22 100%)',
          color: '#fff', boxShadow: '0 12px 32px rgba(255,132,40,0.38)',
          '&:hover': { background: 'linear-gradient(135deg, #ffb06a 0%, #ff8a2a 100%)', transform: 'scale(1.07)' },
          transition: 'transform 0.18s cubic-bezier(0.34,1.26,0.64,1), background 0.18s ease',
        }}>
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Add / Edit modal */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: {
          borderRadius: { xs: '20px !important', sm: '24px !important' },
          background: isDark ? 'rgba(22,14,9,0.97)' : 'rgba(255,253,249,0.97)',
          backdropFilter: 'blur(36px)', WebkitBackdropFilter: 'blur(36px)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.82)',
          boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.55)' : '0 24px 64px rgba(120,60,20,0.14)',
          mx: { xs: 1.5, sm: 'auto' },
          maxHeight: { xs: '90dvh', sm: '85vh' },
          overflowY: 'auto',
        }}}}>

        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.025em' }}>
            {editingId ? t('modal.editTask') : t('modal.addTask')}
          </Typography>
          <IconButton size="small" onClick={closeModal} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>

            {/* Title */}
            <Box>
              <Typography variant="caption" sx={{
                display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary',
                textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.08em',
              }}>
                {t('modal.titleLabel')}
              </Typography>
              <TextField
                value={draft.title}
                onChange={e => { setDraft(d => ({ ...d, title: e.target.value })); setTitleError(false); }}
                fullWidth size="small" required autoFocus
                error={titleError} helperText={titleError ? '✦ Required' : ''}
                slotProps={{ htmlInput: { maxLength: 60 } }}
              />
            </Box>

            {/* Notes */}
            <Box>
              <Typography variant="caption" sx={{
                display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary',
                textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.08em',
              }}>
                {t('modal.descLabel')}
              </Typography>
              <TextField
                value={draft.description}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                fullWidth size="small" multiline rows={2} placeholder={t('modal.descPlaceholder')}
              />
            </Box>

            {/* Priority */}
            <Box>
              <Typography variant="caption" sx={{
                display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary',
                textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.08em',
              }}>
                {t('modal.priority')}
              </Typography>
              <Stack direction="row" spacing={1}>
                {(['low', 'medium', 'high'] as Priority[]).map(p => {
                  const active = draft.priority === p;
                  const activeBg = p === 'low'
                    ? (isDark ? 'rgba(255,248,239,0.18)' : 'rgba(255,255,255,0.9)')
                    : p === 'medium'
                      ? (isDark ? 'rgba(255,138,42,0.24)' : 'rgba(255,220,172,0.7)')
                      : (isDark ? 'rgba(255,100,20,0.34)' : 'rgba(255,165,72,0.7)');
                  const activeBorder = p === 'low'
                    ? (isDark ? 'rgba(255,248,239,0.32)' : 'rgba(0,0,0,0.14)')
                    : p === 'medium' ? 'rgba(255,138,42,0.55)' : 'rgba(255,100,20,0.65)';
                  return (
                    <Chip key={p} label={t(`priority.${p}`)}
                      onClick={() => setDraft(d => ({ ...d, priority: p }))}
                      sx={{
                        flex: 1, cursor: 'pointer', fontWeight: 700, borderRadius: '999px !important',
                        background: active ? activeBg : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                        border: '1.5px solid', borderColor: active ? activeBorder : 'transparent',
                        color: active && p !== 'low' ? '#ff8428' : 'text.primary',
                        transition: 'all 0.15s ease', '&:hover': { opacity: 0.82 },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            {/* Reminder duration */}
            <Box>
              <Typography variant="caption" sx={{
                display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary',
                textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.08em',
              }}>
                {t('modal.duration')}{' '}
                <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
                  ({t('modal.optional')})
                </Box>
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {/* None chip */}
                {(() => {
                  const active = draft.durationMs === null && draft.atTime === null;
                  return (
                    <Chip key="none" label={t('modal.durationNone')}
                      onClick={() => setDraft(d => ({ ...d, durationMs: null, atTime: null }))}
                      sx={{
                        cursor: 'pointer', fontWeight: 700, borderRadius: '999px !important',
                        background: active
                          ? (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')
                          : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)'),
                        border: '1.5px solid',
                        borderColor: active
                          ? (isDark ? 'rgba(255,255,255,0.24)' : 'rgba(0,0,0,0.20)')
                          : 'transparent',
                        color: 'text.primary', '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })()}

                {/* Preset chips */}
                {PRESET_MS.map(p => {
                  const active = draft.durationMs === p.ms && draft.atTime === null;
                  return (
                    <Chip key={p.key} label={t(`modal.preset${p.key}`)}
                      onClick={() => setDraft(d => ({ ...d, durationMs: p.ms, atTime: null }))}
                      sx={{
                        cursor: 'pointer', fontWeight: 700, borderRadius: '999px !important',
                        background: active
                          ? (isDark ? 'rgba(255,138,42,0.26)' : 'rgba(255,220,172,0.72)')
                          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                        border: '1.5px solid',
                        borderColor: active ? 'rgba(255,138,42,0.55)' : 'transparent',
                        color: active ? '#ff8428' : 'text.primary',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })}

                {/* Custom chip */}
                {(() => {
                  const isPreset = PRESET_MS.some(p => p.ms === draft.durationMs);
                  const active   = draft.durationMs !== null && !isPreset && draft.atTime === null;
                  return (
                    <Chip key="custom" label={t('modal.durationCustom')}
                      onClick={() => { if (!active) setDraft(d => ({ ...d, durationMs: 5 * 60 * 1000, atTime: null })); }}
                      sx={{
                        cursor: 'pointer', fontWeight: 700, borderRadius: '999px !important',
                        background: active
                          ? (isDark ? 'rgba(255,138,42,0.26)' : 'rgba(255,220,172,0.72)')
                          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                        border: '1.5px solid',
                        borderColor: active ? 'rgba(255,138,42,0.55)' : 'transparent',
                        color: active ? '#ff8428' : 'text.primary',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })()}

                {/* At time chip */}
                {(() => {
                  const active = draft.atTime !== null;
                  return (
                    <Chip key="attime" label={t('modal.atTime')}
                      onClick={() => {
                        if (!active) {
                          const now = new Date();
                          now.setMinutes(now.getMinutes() + 30);
                          const hh = String(now.getHours()).padStart(2, '0');
                          const mm = String(now.getMinutes()).padStart(2, '0');
                          setDraft(d => ({ ...d, durationMs: null, atTime: `${hh}:${mm}` }));
                        }
                      }}
                      sx={{
                        cursor: 'pointer', fontWeight: 700, borderRadius: '999px !important',
                        background: active
                          ? (isDark ? 'rgba(255,138,42,0.26)' : 'rgba(255,220,172,0.72)')
                          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                        border: '1.5px solid',
                        borderColor: active ? 'rgba(255,138,42,0.55)' : 'transparent',
                        color: active ? '#ff8428' : 'text.primary',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })()}
              </Stack>

              {/* At time — native time picker */}
              {draft.atTime !== null && (() => {
                const [hh, mm] = draft.atTime.split(':').map(Number);
                const target = new Date(); target.setHours(hh, mm, 0, 0);
                if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
                const isToday = target.toDateString() === new Date().toDateString();
                const label   = `${isToday ? t('modal.atTimeToday') : t('modal.atTimeTomorrow')} ${target.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                return (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: 'center' }}>
                    <Box
                      component="input"
                      type="time"
                      value={draft.atTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setDraft(d => ({ ...d, atTime: e.target.value }))
                      }
                      sx={{
                        flex: 1,
                        px: 1.5, py: 1,
                        borderRadius: '10px',
                        border: '1.5px solid',
                        borderColor: isDark ? 'rgba(255,138,42,0.40)' : 'rgba(255,138,42,0.35)',
                        background: isDark ? 'rgba(255,138,42,0.08)' : 'rgba(255,230,190,0.30)',
                        color: 'text.primary',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        outline: 'none',
                        cursor: 'pointer',
                        colorScheme: isDark ? 'dark' : 'light',
                        '&:focus': { borderColor: '#ff8428' },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#ff8428', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                  </Stack>
                );
              })()}

              {/* Custom h/m inputs */}
              {draft.durationMs !== null && draft.atTime === null && !PRESET_MS.some(p => p.ms === draft.durationMs) && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: 'center' }}>
                  <TextField
                    type="number" size="small" label={t('modal.durationHours')}
                    value={Math.floor((draft.durationMs ?? 0) / (60 * 60 * 1000))}
                    onChange={e => {
                      const h = Math.max(0, Math.min(48, Number(e.target.value) || 0));
                      const curMins = Math.floor(((draft.durationMs ?? 0) % (60 * 60 * 1000)) / 60000);
                      setDraft(d => ({ ...d, durationMs: (h * 60 + curMins) * 60 * 1000 }));
                    }}
                    slotProps={{ htmlInput: { min: 0, max: 48, step: 1 } }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="number" size="small" label={t('modal.durationMinutes')}
                    value={Math.floor(((draft.durationMs ?? 0) % (60 * 60 * 1000)) / 60000)}
                    onChange={e => {
                      const m = Math.max(0, Math.min(59, Number(e.target.value) || 0));
                      const curHours = Math.floor((draft.durationMs ?? 0) / (60 * 60 * 1000));
                      setDraft(d => ({ ...d, durationMs: (curHours * 60 + m) * 60 * 1000 }));
                    }}
                    slotProps={{ htmlInput: { min: 0, max: 59, step: 1 } }}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              )}

              {(draft.durationMs !== null || draft.atTime !== null) && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  🔔 {t('modal.notifNote')}
                </Typography>
              )}
            </Box>

          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          {editingId && (
            <Button onClick={handleDelete} color="error" variant="outlined" size="small"
              sx={{ borderRadius: '999px', mr: 'auto' }}>
              {t('modal.delete')}
            </Button>
          )}
          <Button onClick={closeModal} sx={{ borderRadius: '999px', color: 'text.secondary' }}>
            {t('modal.cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!draft.title.trim()} sx={{
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #ff9a45 0%, #ff7d22 100%)',
            color: '#1a0e08', fontWeight: 700, px: 3,
            boxShadow: '0 8px 20px rgba(255,132,40,0.30)',
            '&:hover': { background: 'linear-gradient(135deg, #ffb06a 0%, #ff8a2a 100%)' },
            '&.Mui-disabled': { opacity: 0.5 },
          }}>
            {t('modal.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <LanguageProvider>
      <AntiforgetApp />
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
